import type {Database} from '$lib/classes/interfaces/database';
import {add, differenceInCalendarDays, format} from 'date-fns';
import type {User} from 'firebase/auth';
import {timer} from '$lib/utils/timer';

export type ProVersionDocDB = {
    until: string
    orderId: string | null
}

export type ProVersionDocLocal = ProVersionDocDB & {
    active: boolean,
    untilDate: Date,
}

export class ProVersion {

    private _doc: ProVersionDocLocal | null = $state(null)
    get doc() { return this._doc }

    readonly hasActiveTrial = $derived(Boolean(this._doc && this._doc.active && this._doc.orderId === null))
    readonly hasActivePaidProVersion = $derived(Boolean(this._doc && this._doc.active && this._doc.orderId !== null))

    private cancelDocSubscription = () => {}
    private interval: ReturnType<typeof setInterval> | undefined = undefined

    constructor(
        private readonly database: Database
    ) {
    }

    private calculateActive(until: string) {
        return differenceInCalendarDays(new Date(), new Date(until + 'T00:00:00')) <= 0
    }

    private updateActive() {
        if (this._doc) {
            this._doc.active = this.calculateActive(this._doc.until)
        }
    }

    createInitialDoc(user: User): ProVersionDocDB {
        const creationTime = user.metadata.creationTime
        if (!creationTime) {
            throw new Error('ERROR proVersion.writeInitialDoc - no creationTime')
        }
        const createdAt = new Date(user.metadata.creationTime)
        const until = format(add(createdAt, {weeks: 1}), 'yyyy-MM-dd')
        return {
            orderId: null,
            until
        }
    }

    async init(user: User): Promise<void> {
        const timer_proVersion = timer('proVersion.init')

        let proVersionDoc: ProVersionDocDB | null

        try {
            proVersionDoc = await this.database.getProVersionDoc(user.uid)
        } catch (error) {
            throw new Error('ERROR proVersion.init - getProVersionDoc', {cause: error})
        }

        if (proVersionDoc === null) {
            try {
                const initialDoc = this.createInitialDoc(user)
                await this.database.setProVersionDoc(user.uid, initialDoc)
            } catch (error) {
                throw new Error('ERROR proVersion.init - set initial proVersionDoc', {cause: error})
            }
        }

        try {
            const {unsubscribe, firstValue} = this.database.subscribeProVersionDoc(
                user.uid,
                (doc: ProVersionDocDB | null) => {
                    if (doc) {
                        const proVersionDocLocal: ProVersionDocLocal = {
                            ...doc,
                            active: this.calculateActive(doc.until),
                            untilDate: new Date(doc.until + 'T00:00:00'), //NOTE important to set time! Otherwise hour shift applied relative to UTC 00:00:00
                        }
                        this._doc = proVersionDocLocal
                    } else {
                        this._doc = null
                    }
                }
            )

            this.cancelDocSubscription = unsubscribe
            await firstValue

        } catch (error) {
            throw new Error('ERROR proVersion.init - subscribeProVersionDoc', {cause: error})
        }

        this.interval = setInterval(() => this.updateActive(), 60000)

        timer_proVersion()
    }

    close() {
        this.cancelDocSubscription()
        clearInterval(this.interval)
        this._doc = null
    }
}